import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import useReveal from "@/hooks/useReveal";

const DEFAULT_SERVICES = [
  {
    key: "architecture",
    title: "Architecture",
    tagline: "Design, planning & documentation",
    body: "Considered, contextual architecture — from concept sketches to municipal drawings and construction sets.",
    image_url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    key: "3d",
    title: "3D Visualisation",
    tagline: "Photoreal renders & animation",
    body: "Bring proposals to life with photoreal stills, flythroughs and immersive material studies.",
    image_url: "https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    key: "construction",
    title: "Construction",
    tagline: "Turn-key project delivery",
    body: "NHBRC-registered construction — programme, procurement and quality management to completion.",
    image_url: "https://images.pexels.com/photos/69483/pexels-photo-69483.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

const PROCESS = [
  "Consultation",
  "Concept & Design",
  "Planning & Documentation",
  "3D Visualisation",
  "Construction",
  "Project Completion",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [site, setSite] = useState(null);
  const ref = useReveal();

  useEffect(() => {
    api.get("/projects/featured").then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/site/content").then((r) => setSite(r.data || {})).catch(() => setSite({}));
  }, []);

  const siteData = site || {};

  return (
    <div ref={ref} data-testid="home-page">
      {/* Hero */}
      <section className="relative h-screen min-h-[720px] w-full overflow-hidden bg-ink" data-testid="hero-section">
        <div className="absolute inset-0">
          {site && siteData.hero_image_url && (
            <img
              src={mediaUrl(siteData.hero_image_url)}
              alt="AS Design Studios featured architecture"
              className="w-full h-full object-cover opacity-90"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
        </div>

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-24">
          <div className="eyebrow text-white/70 mb-6">AS · Design Studios · Cape Town</div>
          <h1 className="font-heading font-light text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter max-w-[16ch]">
            {siteData.hero_title || "From Concept to Construction."}
          </h1>
          <p className="mt-8 max-w-xl text-white/85 text-base sm:text-lg leading-relaxed">
            {siteData.hero_subtitle || "AS Design Studios delivers architectural design, 3D visualisation and construction — a single studio, from the first sketch to the completed building."}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/projects"
              data-testid="hero-projects-btn"
              className="inline-flex items-center gap-2 bg-white text-ink px-7 py-3.5 text-sm hover:bg-white/85 transition-colors"
            >
              View Our Projects <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* corner meta */}
        <div className="absolute bottom-8 right-8 hidden md:flex flex-col items-end gap-1 text-white/60 font-mono text-xs">
          <div>SCROLL —</div>
          <div>01 / 06</div>
        </div>
      </section>

      {/* About */}
      <section className="py-28 lg:py-40 border-b border-line" data-testid="about-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow reveal">About the studio</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="reveal font-heading font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ink">
              {siteData.about_text || "AS Design Studios is a multidisciplinary design-build studio managing projects from concept and planning through visualisation and construction — a single, considered vision from the first sketch to the completed building."}
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-28 lg:py-40" data-testid="services-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-4"><div className="eyebrow reveal">Our services</div></div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="reveal font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
                Three disciplines. <span className="text-stone">One studio.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(siteData.services && siteData.services.length ? siteData.services : DEFAULT_SERVICES).map((s) => (
              <div
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="reveal group border border-line bg-white flex flex-col hover:border-ink transition-colors"
              >
                <div className="zoom-wrap aspect-[4/5] bg-sand">
                  <img src={mediaUrl(s.image_url)} alt={s.title} className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="p-6 lg:p-8 flex-1 flex flex-col">
                  <div className="eyebrow text-stone mb-3">{s.tagline}</div>
                  <h3 className="font-heading text-2xl lg:text-3xl tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-stone leading-relaxed">{s.body}</p>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-1 text-sm text-ink border-b border-ink w-fit pb-1"
                  >
                    Enquire <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-28 lg:py-40 bg-paper-warm" data-testid="featured-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-4"><div className="eyebrow reveal">Selected work</div></div>
            <div className="col-span-12 md:col-span-8 flex items-end justify-between gap-6">
              <h2 className="reveal font-heading font-light text-4xl sm:text-5xl tracking-tight">
                Featured projects
              </h2>
              <Link to="/projects" className="hidden sm:inline-flex items-center gap-2 text-sm border-b border-ink pb-1">
                View all <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-16">
            {featured.slice(0, 4).map((p, idx) => (
              <Link
                to={`/projects/${p.id}`}
                key={p.id}
                data-testid={`featured-project-${p.id}`}
                className={`reveal group block ${idx % 2 === 1 ? "md:mt-24" : ""}`}
              >
                <div className="zoom-wrap bg-sand aspect-[4/3]">
                  <img src={mediaUrl(p.cover_image_url)} alt={p.name} className="zoom-img w-full h-full object-cover" />
                </div>
                <div className="mt-5 flex justify-between items-start gap-6">
                  <div>
                    <h3 className="font-heading text-2xl tracking-tight">{p.name}</h3>
                    <div className="text-sm text-stone mt-1">{p.location} · {p.year}</div>
                  </div>
                  <div className="font-mono text-xs text-stone shrink-0">{p.project_type}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-28 lg:py-40 border-t border-line" data-testid="process-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-4"><div className="eyebrow reveal">The process</div></div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="reveal font-heading font-light text-4xl sm:text-5xl tracking-tight leading-tight">
                Six stages, one continuous conversation.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 border-t border-ink">
            {PROCESS.map((step, i) => (
              <div
                key={step}
                data-testid={`process-step-${i + 1}`}
                className="reveal group py-8 border-b border-line md:border-b-0 md:border-r border-r-line last:border-r-0 hover:bg-sand transition-colors px-2"
              >
                <div className="font-mono text-xs text-stone">{String(i + 1).padStart(2, "0")}</div>
                <div className="mt-6 font-heading text-lg lg:text-xl leading-tight">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-48 bg-ink text-white" data-testid="cta-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 text-left">
          <div className="eyebrow text-white/50 mb-6">Let's talk</div>
          <h2 className="font-heading font-light text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-[18ch]">
            Have a project in mind? Let's <em className="not-italic text-white/60">design it together.</em>
          </h2>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="mt-12 inline-flex items-center gap-3 bg-white text-ink px-8 py-4 text-sm hover:bg-white/85 transition-colors"
          >
            Start a conversation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
